import {useQuery} from '@tanstack/react-query';
import {growthbookFeatureIdToKey} from 'atoms/growthbook';
import {growthbookCacheAtom} from 'atoms/layout';
import {
  AiWorkstreamHealthScoreService,
  type EmployeeRoleLevel,
  type Workstream,
  type WorkstreamCheckResponse,
  type WorkstreamHealthScoreResponse,
  type WorkstreamMetric,
  type WorkstreamOutcomesValidationCheckResponse,
} from 'client';
import {parseAndExtractTextFromLexicalJson} from 'components/DSYS/editor/utils/lexical';
import {useAtomValue} from 'jotai';
import {useMemo} from 'react';
import {getService} from 'utilities';
import {useDefaultWorkstreamTimeframe} from 'views/workstreams/detail/hooks';
import {getWorkstreamDescriptionPlainText} from 'views/workstreams/detail/workstreamDescriptionRichText';
import {getDefaultWorkstreamTimeframe} from 'views/workstreams/detail/workstreamTimeframe';

import {
  getWorkstreamExecSponsor,
  isClosedMetricStatus,
  isNorthStarMetricType,
  isPrimaryMetric,
} from './utils';

export type DraftMetric = {
  draftId?: string;
  metricId?: number | null;
  type: 'metric' | 'milestone' | 'deliverable';
  titleText: string;
  owner: EmployeeRoleLevel | null;
  baseline?: string | null;
  current?: string | null;
  status?: string | null;
  complete?: boolean | null;
  metricType: 'leading' | 'lagging' | 'other';
  metricForecasts?: Record<string, {budget?: string | null}>;
  metric_forecasts?: WorkstreamMetric['metric_forecasts'];
  isNew?: boolean;
  isPendingAttach?: boolean;
};

export type WorkstreamCheckType =
  | 'health_score'
  | 'outcomes_validation_metrics'
  | 'outcomes_validation_outcomes';

export const getMetricOutcomesValidationQueryKey = (metricId: string) => [
  'workstream',
  'outcomes_validation',
  metricId,
];

export type WorkstreamCheckSource =
  | 'ai_only' // Evaluated solely by AI.
  | 'local_only' // Evaluated deterministically on client and server; client result wins.
  | 'local_precheck_and_ai' // A deterministic failure blocks AI evaluation.
  | 'server_local_check'; // Evaluated deterministically on the server; saved result wins.
export type WorkstreamCheckResultSource = NonNullable<WorkstreamCheckResponse['resultSource']>;
type WorkstreamCheckResponseWithFailureCopy = WorkstreamCheckResponse & {
  failureCopy?: string;
};
type WorkstreamWithMnpiServerLocalHealthScoreChecks = Workstream & {
  mnpi_server_local_health_score_checks?: WorkstreamCheckResponse[] | null;
};
export type WorkstreamCheckResponseWithSource = Omit<
  WorkstreamCheckResponseWithFailureCopy,
  'resultSource'
> & {
  resultSource?: WorkstreamCheckResultSource | null;
  isAIPending?: boolean;
};

type WorkstreamCheckFailureCopy = string | (() => string);
type HealthMetric = WorkstreamMetric;

export const MNPI_SERVER_LOCAL_HEALTH_SCORE_FAILURE_COPY =
  'Evaluating confidential metrics, which have restricted audiences.';

type WorkstreamCheckDefinition = {
  description: string;
  checkTypes: WorkstreamCheckType[];
  failureCopy: WorkstreamCheckFailureCopy;
  checkSource: WorkstreamCheckSource;
  growthbookFeatureId?: string;
};

type MetricWithBudgetForecasts = {
  metricForecasts?: Record<string, {budget?: string | null}>;
  metric_forecasts?: Array<{period?: string | null; budget?: string | null}> | null;
};

type MetricForecastValueFields = {
  actual?: string | null;
  target?: string | null;
  budget?: string | null;
  archived_at?: string | null;
};

type BudgetHalf = {
  half: 'H1' | 'H2';
  relation: 'current' | 'upcoming';
  quarters: number[];
  year: number;
};

const FINAL_HALF_PLANNING_WINDOW_DAYS = 14;
const ALLOWED_NON_NUMERIC_BASELINE_VALUES = new Set(['New Metric']);
const ALLOWED_NON_NUMERIC_CURRENT_VALUES = new Set(['Pending']);
const ALLOWED_NON_NUMERIC_FORECAST_VALUES = new Set(['N/A', 'NA', 'Pending']);

/**
 * Server check definitions are the source of truth for persisted local checks.
 * Keep this in sync with helpers/workstream_health_checks.py::WORKSTREAM_HEALTH_CHECK_DEFINITIONS
 * and helpers/workstream_outcomes_validation_checks.py::OUTCOMES_VALIDATION_CHECK_DEFINITIONS.
 */
export const WORKSTREAM_CHECK_DEFINITIONS: Record<string, WorkstreamCheckDefinition> = {
  // Health score checks
  objective_check: {
    description: 'Objective is clear and customer focused',
    checkTypes: ['health_score'],
    failureCopy:
      'The objective should clearly explain what we are trying to do and why (should mention customer or stakeholder benefit). It should be written in plain English.',
    checkSource: 'local_precheck_and_ai',
  },
  exec_sponsor_check: {
    description: 'Workstream has an exec sponsor',
    checkTypes: ['health_score'],
    failureCopy: 'Add exec sponsor so leadership accountability is explicit.',
    checkSource: 'local_only',
  },
  metric_milestone_check: {
    description: 'At least one metric or milestone is defined',
    checkTypes: ['health_score'],
    failureCopy:
      'Add at least one metric or milestone to show how this workstream will be evaluated.',
    checkSource: 'local_only',
  },
  leading_lagging_metric_check: {
    description: 'All metrics are labeled as leading or lagging',
    checkTypes: ['health_score'],
    failureCopy:
      'Tag each metric as leading (tracks near-term progress) or lagging (measures business results like NRR or NNARR).',
    checkSource: 'local_only',
  },
  north_star_metric_check: {
    description: 'Exactly one North Star Metric is starred',
    checkTypes: ['health_score'],
    failureCopy:
      'Star the single leading or lagging metric that best represents whether this workstream succeeded.',
    checkSource: 'local_only',
  },
  metric_value_check: {
    description: 'Metric values use accepted formats',
    checkTypes: ['health_score'],
    failureCopy:
      'Values must be either numeric or accepted alternatives. No "TBD" or other free text.',
    checkSource: 'local_only',
  },
  metric_baseline_budget_value_check: {
    description: 'Every metric has a baseline and budget value',
    checkTypes: ['health_score'],
    failureCopy: getMetricBaselineBudgetFailureCopy,
    checkSource: 'local_only',
  },
  milestone_criteria_check: {
    description: 'Every milestone has a clear binary criterion',
    failureCopy:
      'Each milestone should describe something that either happened or didn\'t. Ideally frame it as a customer or stakeholder result, not an activity. Start with a verb like "Launch," "Ship," "Complete," "Define," or "Close.”',
    checkTypes: ['health_score'],
    checkSource: 'server_local_check',
  },
  // Outcomes validation checks
  metric_baseline_value_check: {
    description: 'Baseline and current values must be numeric',
    checkTypes: ['outcomes_validation_metrics'],
    failureCopy:
      'Use "New Metric" for baseline if there\'s no prior actual to baseline against. Use "Pending" for current if awaiting initial data.',
    checkSource: 'local_only',
  },
  metric_actual_forecast_value_check: {
    description: 'Actual and forecast values must use accepted formats',
    checkTypes: ['outcomes_validation_metrics'],
    failureCopy:
      'Values must be either numeric, "N/A" (doesn\'t apply), or "Pending" (awaiting data). No "TBD" or other free text.',
    checkSource: 'local_only',
  },
  outcomes_milestone_reasonable_binary_check: {
    description: 'Milestones are reasonably binary',
    checkTypes: ['outcomes_validation_outcomes'],
    failureCopy:
      'Milestones should be binary — it either happened or it didn\'t. Start with a clear action verb like "Launch," "Ship," "Complete," "Define," or "Close."',
    checkSource: 'local_precheck_and_ai',
  },
  outcomes_milestone_concise_check: {
    description: 'Milestones are concise and jargon-free',
    checkTypes: ['outcomes_validation_outcomes'],
    failureCopy:
      'Remove business jargon and acronyms. Milestones should be under 30 words and clear to anyone on the team.',
    checkSource: 'local_precheck_and_ai',
  },
};

export const useEnabledGrowthbookFeatureIds = () => {
  const featureFlags = useAtomValue(growthbookCacheAtom);
  return useMemo(
    () =>
      new Set(
        Object.values(WORKSTREAM_CHECK_DEFINITIONS).flatMap((definition) => {
          const featureId = definition.growthbookFeatureId;
          const featureKey = featureId ? growthbookFeatureIdToKey[featureId] : undefined;
          return featureId && featureKey && featureFlags[featureKey] ? [featureId] : [];
        })
      ),
    [featureFlags]
  );
};

export const getExpectedHealthScoreCheckCount = (
  enabledGrowthbookFeatureIds: ReadonlySet<string> = new Set()
) =>
  Object.values(WORKSTREAM_CHECK_DEFINITIONS).filter(
    (definition) =>
      definition.checkTypes.includes('health_score') &&
      (!definition.growthbookFeatureId ||
        enabledGrowthbookFeatureIds.has(definition.growthbookFeatureId))
  ).length;

const WORKSTREAM_CHECK_ORDER = new Map(
  Object.keys(WORKSTREAM_CHECK_DEFINITIONS).map((checkId, index) => [checkId, index])
);

const READINESS_CORE_PILLAR_LEVELS = new Set([1, 2]);
const TOP_50_OR_SUB_WORKSTREAM_LEVELS = new Set([1, 2, 3]);

export const isCorePillarLevel = (pillarLevel?: number | null) =>
  typeof pillarLevel === 'number' && READINESS_CORE_PILLAR_LEVELS.has(pillarLevel);

export const isTop50OrSubWorkstream = (pillarLevel?: number | null) =>
  typeof pillarLevel === 'number' && TOP_50_OR_SUB_WORKSTREAM_LEVELS.has(pillarLevel);

export const shouldBlockOnTrackForReadiness = (
  healthScore?: WorkstreamHealthScoreResponse | null
) => {
  const pillarLevel = healthScore?.workstream_pillar_metadata?.pillar_level;
  const score = healthScore?.score?.toLowerCase();
  return isCorePillarLevel(pillarLevel) && !!score && score !== 'exemplary';
};

export const calculateHealthScore = (
  checks: Array<Pick<WorkstreamCheckResponseWithSource, 'passed'>>
) => {
  const failedCheckCount = checks.filter((check) => check.passed === false).length;
  if (failedCheckCount === 0) {
    return 'exemplary';
  }
  if (failedCheckCount <= 2) {
    return 'adequate';
  }
  return 'poor';
};

export const isValidWorkstreamHealthScore = (
  healthScore: WorkstreamHealthScoreResponse | null | undefined,
  enabledGrowthbookFeatureIds: ReadonlySet<string> = new Set()
): boolean => {
  const expectedCheckCount =
    healthScore?.criteria_check_ids?.length ??
    getExpectedHealthScoreCheckCount(enabledGrowthbookFeatureIds);
  const hasValidCheckCount = (healthScore?.checks?.length ?? 0) === expectedCheckCount;
  const hasConsistentScore =
    healthScore?.score?.toLowerCase() === calculateHealthScore(healthScore?.checks ?? []);

  return !!healthScore?.task_id && !!healthScore.score && hasValidCheckCount && hasConsistentScore;
};

export const useWorkstreamHealthScore = ({
  workstreamId,
  checksType,
  enabled = true,
}: {
  workstreamId: number;
  checksType: 'default' | 'oceo';
  enabled?: boolean;
}) => {
  const enabledGrowthbookFeatureIds = useEnabledGrowthbookFeatureIds();
  const shouldShowHealthScore = useShouldShowWorkstreamHealthScore(workstreamId, enabled);
  const timeframe = useDefaultWorkstreamTimeframe();
  const {data, isLoading} = useQuery({
    queryKey: ['workstream', 'health_score', workstreamId, checksType, timeframe],
    queryFn: () =>
      getService(
        AiWorkstreamHealthScoreService
      ).getWorkstreamHealthScoreApiV1WorkstreamIdHealthScoreGet(
        workstreamId,
        checksType,
        timeframe
      ),
    enabled: Boolean(workstreamId > 0 && enabled && shouldShowHealthScore && timeframe),
    retry: false,
  });
  const healthScore = isValidWorkstreamHealthScore(data, enabledGrowthbookFeatureIds) ? data : null;
  return {healthScore, rawHealthScore: data ?? null, isLoading, shouldShowHealthScore};
};

export const useWorkstreamHealthScoreCounts = (workstream: Workstream) => {
  const enabledGrowthbookFeatureIds = useEnabledGrowthbookFeatureIds();
  const {healthScore} = useWorkstreamHealthScore({
    workstreamId: workstream.id,
    checksType: 'default',
  });
  return useMemo(
    () => getHealthScoreCounts({workstream, healthScore, enabledGrowthbookFeatureIds}),
    [enabledGrowthbookFeatureIds, healthScore, workstream]
  );
};

export const useBlockOnTrackForReadiness = (workstreamId: number) => {
  const {healthScore} = useWorkstreamHealthScore({
    workstreamId,
    checksType: 'default',
  });
  return shouldBlockOnTrackForReadiness(healthScore);
};

// fetchWorkstream seeds this from the workstream's health_score_enabled, so
// the readiness slot is known when the row first renders.
export const getWorkstreamHealthScoreEnabledQueryKey = (
  workstreamId: number,
  timeframe = getDefaultWorkstreamTimeframe()
) => ['workstream', 'health_score', 'enabled', workstreamId, timeframe];

export const useShouldShowWorkstreamHealthScore = (
  workstreamId: number,
  enabled: boolean = true
): boolean => {
  const timeframe = useDefaultWorkstreamTimeframe();
  const {data: isWorkstreamEnabled} = useQuery({
    queryKey: getWorkstreamHealthScoreEnabledQueryKey(workstreamId, timeframe),
    queryFn: () =>
      getService(
        AiWorkstreamHealthScoreService
      ).checkWorkstreamHealthScoreEnabledApiV1WorkstreamIdHealthScoreEnabledGet(
        workstreamId,
        timeframe
      ),
    // Wait for the timeframes rather than ask about an empty one.
    enabled: workstreamId > 0 && enabled && Boolean(timeframe),
  });
  return isWorkstreamEnabled ?? false;
};

export const shouldShowOutcomesValidation = (
  metric: WorkstreamOutcomesValidationMetric
): boolean => {
  const title = getOutcomeTitleText(metric);
  if (metric.type === 'milestone') {
    const status = (metric.status ?? '').trim().toLowerCase();
    return !!title && metric.complete !== true && !['complete', 'completed'].includes(status);
  }
  if (metric.type === 'metric') {
    if (isClosedMetricStatus(metric.status)) {
      return false;
    }
    if (metric.isNew) {
      return !!title && metric.metricType !== 'other';
    }
    return !!title;
  }
  // Outcomes validation is not available for deliverables.
  return false;
};

const getCurrentBudgetHalf = (referenceDate: Date): BudgetHalf => {
  const year = referenceDate.getFullYear();
  if (referenceDate.getMonth() < 6) {
    return {half: 'H1', relation: 'current', quarters: [1, 2], year};
  }

  return {half: 'H2', relation: 'current', quarters: [3, 4], year};
};

const getHalfEndDate = (referenceDate: Date) => {
  const year = referenceDate.getFullYear();
  return referenceDate.getMonth() < 6 ? new Date(year, 5, 30) : new Date(year, 11, 31);
};

const startOfLocalDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const isInFinalHalfBudgetWindow = (referenceDate: Date) => {
  const today = startOfLocalDay(referenceDate);
  const halfEndDate = getHalfEndDate(today);
  const windowStartDate = new Date(halfEndDate);
  windowStartDate.setDate(halfEndDate.getDate() - FINAL_HALF_PLANNING_WINDOW_DAYS + 1);

  return today >= windowStartDate && today <= halfEndDate;
};

const getRequiredBudgetHalf = (referenceDate = new Date()): BudgetHalf => {
  const currentHalf = getCurrentBudgetHalf(referenceDate);
  if (!isInFinalHalfBudgetWindow(referenceDate)) {
    return currentHalf;
  }

  if (currentHalf.half === 'H1') {
    return {half: 'H2', relation: 'upcoming', quarters: [3, 4], year: currentHalf.year};
  }

  return {half: 'H1', relation: 'upcoming', quarters: [1, 2], year: currentHalf.year + 1};
};

const getRequiredBudgetHalfCopy = () => {
  const budgetHalf = getRequiredBudgetHalf();
  return `${budgetHalf.relation} half, eg ${budgetHalf.half} ${budgetHalf.year}`;
};

function getMetricBaselineBudgetFailureCopy() {
  return `Each metric needs a baseline (your agreed-upon starting point) and a budget value (your “Plan of Record” for the ${getRequiredBudgetHalfCopy()}).`;
}

export const getFailureCopy = (id: string): string | undefined => {
  const failureCopy = WORKSTREAM_CHECK_DEFINITIONS[id]?.failureCopy;
  if (!failureCopy) return undefined;

  return typeof failureCopy === 'function' ? failureCopy() : failureCopy;
};

export const buildLocalCheck = (
  id: string,
  passed: boolean
): WorkstreamCheckResponseWithFailureCopy => {
  return {
    id,
    passed,
    failureCopy: getFailureCopy(id),
  };
};

const markLocalCheckResult = (
  check: WorkstreamCheckResponseWithFailureCopy
): WorkstreamCheckResponseWithSource => {
  return {
    id: check.id,
    passed: check.passed,
    failureCopy: check.failureCopy,
    rationale: undefined, // Reserved for AI rationale
    resultSource: 'local',
  };
};

const isWorkstreamCheckType = (checkId: string, checksType: WorkstreamCheckType) =>
  WORKSTREAM_CHECK_DEFINITIONS[checkId]?.checkTypes.includes(checksType) === true;

export const getWorkstreamCheckSource = (
  checkId: string,
  checksType: WorkstreamCheckType
): WorkstreamCheckSource =>
  isWorkstreamCheckType(checkId, checksType)
    ? WORKSTREAM_CHECK_DEFINITIONS[checkId].checkSource
    : 'ai_only';

export const getWorkstreamCheckDescription = (checkId: string) => {
  return WORKSTREAM_CHECK_DEFINITIONS[checkId]?.description ?? checkId;
};

export const mergeChecksWithLocalData = ({
  checks,
  localChecks,
  checksType,
}: {
  checks: WorkstreamCheckResponseWithSource[];
  localChecks: WorkstreamCheckResponseWithFailureCopy[];
  checksType: WorkstreamCheckType;
}): WorkstreamCheckResponseWithSource[] => {
  const localChecksById = new Map(localChecks.map((check) => [check.id, check]));
  const seenCheckIds = new Set<string>();

  const mergedChecks = checks.map((check) => {
    seenCheckIds.add(check.id);
    const checkSource = getWorkstreamCheckSource(check.id, checksType);

    if (checkSource === 'local_only') {
      return markLocalCheckResult(
        localChecksById.get(check.id) ?? buildLocalCheck(check.id, false)
      );
    }

    if (checkSource === 'server_local_check') {
      return check;
    }

    if (checkSource === 'local_precheck_and_ai') {
      const localCheck = localChecksById.get(check.id);
      return localCheck?.passed === false ? markLocalCheckResult(localCheck) : check;
    }

    return check;
  });

  localChecksById.forEach((check, checkId) => {
    const checkSource = getWorkstreamCheckSource(checkId, checksType);
    const shouldAppendLocalCheck =
      checkSource === 'local_only' ||
      (checkSource === 'local_precheck_and_ai' && check.passed === false);
    if (!seenCheckIds.has(checkId) && shouldAppendLocalCheck) {
      mergedChecks.push(markLocalCheckResult(check));
    }
  });

  return mergedChecks;
};

const buildPendingCheck = (checkId: string): WorkstreamCheckResponseWithSource => ({
  id: checkId,
  passed: false,
  isAIPending: true,
});

/**
 * Resolves the configured checks applicable to a UI surface. Callers supply
 * GrowthBook feature IDs already evaluated by the client.
 */
export const getWorkstreamCheckIdsForType = (
  checksType: WorkstreamCheckType,
  enabledGrowthbookFeatureIds: ReadonlySet<string> = new Set()
) =>
  Object.entries(WORKSTREAM_CHECK_DEFINITIONS)
    .filter(
      ([, definition]) =>
        definition.checkTypes.includes(checksType) &&
        (!definition.growthbookFeatureId ||
          enabledGrowthbookFeatureIds.has(definition.growthbookFeatureId))
    )
    .map(([checkId]) => checkId);

export const getHealthScoreCriteria = <Check extends {id: string}>(
  workstream: Workstream,
  checks: Check[],
  enabledGrowthbookFeatureIds: ReadonlySet<string> = new Set(),
  savedCriteriaCheckIds?: readonly string[] | null
): [string[] | null, Check[]] => {
  if (savedCriteriaCheckIds !== undefined) {
    if (savedCriteriaCheckIds === null) {
      return [null, checks];
    }
    const savedCriteriaCheckIdsSet = new Set(savedCriteriaCheckIds);
    return [
      [...savedCriteriaCheckIds],
      checks.filter((check) => savedCriteriaCheckIdsSet.has(check.id)),
    ];
  }

  const enabledHealthScoreCheckIds = new Set(
    getWorkstreamCheckIdsForType('health_score', enabledGrowthbookFeatureIds)
  );

  const criteriaCheckIds = ['objective_check', 'exec_sponsor_check', 'metric_milestone_check'];
  if (getVisibleHealthMetrics(workstream).length) {
    criteriaCheckIds.push(
      'leading_lagging_metric_check',
      'north_star_metric_check',
      'metric_value_check',
      'metric_baseline_budget_value_check'
    );
  }
  if (getVisibleMilestones(workstream).length) {
    criteriaCheckIds.push('milestone_criteria_check');
  }

  const enabledCriteriaCheckIds = criteriaCheckIds.filter((checkId) =>
    enabledHealthScoreCheckIds.has(checkId)
  );
  if (!enabledCriteriaCheckIds.length) {
    return [null, checks];
  }

  const enabledCriteriaCheckIdsSet = new Set(enabledCriteriaCheckIds);
  return [
    enabledCriteriaCheckIds,
    checks.filter((check) => enabledCriteriaCheckIdsSet.has(check.id)),
  ];
};

export type HealthScoreCounts = {
  completedCount: number;
  totalCount: number;
};

export const getHealthScoreCounts = ({
  workstream,
  healthScore,
  enabledGrowthbookFeatureIds = new Set(),
}: {
  workstream: Workstream;
  healthScore?: WorkstreamHealthScoreResponse | null;
  enabledGrowthbookFeatureIds?: ReadonlySet<string>;
}): HealthScoreCounts | null => {
  const [criteriaCheckIds, criteriaChecks] = getHealthScoreCriteria(
    workstream,
    healthScore?.checks ?? [],
    enabledGrowthbookFeatureIds,
    healthScore?.criteria_check_ids
  );
  const totalCount = criteriaCheckIds?.length ?? criteriaChecks.length;
  if (!totalCount) {
    return null;
  }
  return {
    completedCount: criteriaChecks.filter((check) => check.passed).length,
    totalCount,
  };
};

const buildMissingDefinitionCheck = (checkId: string) => buildLocalCheck(checkId, false);

const withConfiguredFailureCopy = (
  check: WorkstreamCheckResponseWithSource
): WorkstreamCheckResponseWithSource => {
  const failureCopy = check.failureCopy ?? getFailureCopy(check.id);
  return failureCopy ? {...check, failureCopy} : check;
};

const withConfiguredResultSource = (
  check: WorkstreamCheckResponseWithSource,
  checksType: WorkstreamCheckType
): WorkstreamCheckResponseWithSource => {
  const checkSource = getWorkstreamCheckSource(check.id, checksType);
  return checkSource === 'local_only' || checkSource === 'server_local_check'
    ? {...check, resultSource: 'local'}
    : check;
};

export const getChecksForDisplay = (
  checks: WorkstreamCheckResponseWithSource[],
  checksType: WorkstreamCheckType,
  enabledGrowthbookFeatureIds: ReadonlySet<string> = new Set()
): WorkstreamCheckResponseWithSource[] => {
  const checksForType = checks.filter(
    (check) =>
      isWorkstreamCheckType(check.id, checksType) &&
      getWorkstreamCheckIdsForType(checksType, enabledGrowthbookFeatureIds).includes(check.id)
  );
  const checksById = new Map(checksForType.map((check) => [check.id, check]));
  const definedCheckIds = getWorkstreamCheckIdsForType(checksType, enabledGrowthbookFeatureIds);
  const definedCheckIdsSet = new Set(definedCheckIds);
  const definedChecks = definedCheckIds.map(
    (checkId) => checksById.get(checkId) ?? buildMissingDefinitionCheck(checkId)
  );
  const extraChecks = checksForType.filter((check) => !definedCheckIdsSet.has(check.id));

  return [...definedChecks, ...extraChecks]
    .map((check) => withConfiguredResultSource(check, checksType))
    .map(withConfiguredFailureCopy)
    .sort(
      (firstCheck, secondCheck) =>
        (WORKSTREAM_CHECK_ORDER.get(firstCheck.id) ?? Number.MAX_SAFE_INTEGER) -
        (WORKSTREAM_CHECK_ORDER.get(secondCheck.id) ?? Number.MAX_SAFE_INTEGER)
    );
};

const getVisibleMetrics = (workstream: Workstream) =>
  workstream.metrics.filter((metric) => metric.type === 'metric');

const getVisibleHealthMetrics = (workstream: Workstream): HealthMetric[] =>
  getVisibleMetrics(workstream);

const getVisibleMilestones = (workstream: Workstream) =>
  workstream.metrics.filter((metric) => metric.type === 'milestone');

const getVisibleMetricsOrMilestones = (workstream: Workstream): HealthMetric[] => [
  ...workstream.metrics.filter((metric) => metric.type === 'metric' || metric.type === 'milestone'),
];

const getOpenHealthMetrics = (metrics: HealthMetric[]) =>
  metrics.filter((metric) => !isClosedMetricStatus(metric.status));

const hasNumericText = (value: string | null | undefined) => {
  const normalizedValue = parseAndExtractTextFromLexicalJson(value ?? '')
    .trim()
    .replace(/,/g, '');
  return /-?(?:\d+\.?\d*|\.\d+)/.test(normalizedValue);
};

const hasNonEmptyText = (value: string | null | undefined) =>
  Boolean(parseAndExtractTextFromLexicalJson(value ?? '').trim());

const hasNonNumericText = (
  value: string | null | undefined,
  options?: {
    allowedValues?: ReadonlySet<string>;
  }
) => {
  const text = parseAndExtractTextFromLexicalJson(value ?? '').trim();
  const isAllowedValue = [...(options?.allowedValues ?? [])].some(
    (allowedValue) => allowedValue.toLowerCase() === text.toLowerCase()
  );
  return Boolean(text) && !hasNumericText(text) && !isAllowedValue;
};

export const hasInvalidMetricBaselineValue = (
  value: string | null | undefined,
  options?: {requireExists?: boolean}
) => {
  if (options?.requireExists && !hasNonEmptyText(value)) {
    return true;
  }

  return hasNonNumericText(value, {
    allowedValues: ALLOWED_NON_NUMERIC_BASELINE_VALUES,
  });
};

export const hasInvalidMetricCurrentValue = (value: string | null | undefined) =>
  hasNonNumericText(value, {
    allowedValues: ALLOWED_NON_NUMERIC_CURRENT_VALUES,
  });

const hasInvalidMetricForecastValue = (value: string | null | undefined) =>
  hasNonNumericText(value, {
    allowedValues: ALLOWED_NON_NUMERIC_FORECAST_VALUES,
  });

export const isRequiredBudgetPeriod = (
  period: string | null | undefined,
  requiredBudgetHalf = getRequiredBudgetHalf()
) => {
  const match = /^(\d{4})(?:Q([1-4])|(EOY))$/.exec(period?.trim() ?? '');
  if (!match) return false;

  const periodYear = Number(match[1]);
  if (periodYear !== requiredBudgetHalf.year) {
    return false;
  }

  if (match[3] === 'EOY') {
    return requiredBudgetHalf.half === 'H2';
  }

  const periodQuarter = Number(match[2]);
  if (!periodQuarter) return false;
  return requiredBudgetHalf.quarters.includes(periodQuarter);
};

export const hasBudgetForRequiredHalf = (
  metric: MetricWithBudgetForecasts,
  requiredBudgetHalf = getRequiredBudgetHalf()
) => {
  if (metric.metricForecasts) {
    return Object.entries(metric.metricForecasts).some(
      ([period, forecast]) =>
        isRequiredBudgetPeriod(period, requiredBudgetHalf) && hasNonEmptyText(forecast.budget)
    );
  }

  return (metric.metric_forecasts ?? []).some(
    (forecast) =>
      isRequiredBudgetPeriod(forecast.period, requiredBudgetHalf) &&
      hasNonEmptyText(forecast.budget)
  );
};

const getLocalObjectivePrecheck = (
  workstream: Workstream
): WorkstreamCheckResponseWithFailureCopy => {
  const objective = getWorkstreamDescriptionPlainText(workstream.description).trim();
  return buildLocalCheck('objective_check', objective.length > 0);
};

const getLocalExecSponsorCheck = (workstream: Workstream) =>
  buildLocalCheck('exec_sponsor_check', Boolean(getWorkstreamExecSponsor(workstream)));

const getLocalNorthStarMetricCheck = (workstream: Workstream) => {
  const primaryMetrics = getVisibleMetrics(workstream).filter(
    (metric) => isNorthStarMetricType(metric.metric_type) && isPrimaryMetric(metric, workstream.id)
  );
  const count = primaryMetrics.length;

  return buildLocalCheck('north_star_metric_check', count === 1);
};

const getLocalLeadingLaggingMetricCheck = (workstream: Workstream) => {
  const metrics = getVisibleHealthMetrics(workstream);
  if (!metrics.length) {
    return buildLocalCheck('leading_lagging_metric_check', false);
  }

  const missingMetricType = getOpenHealthMetrics(metrics).filter(
    (metric) => metric.metric_type !== 'leading' && metric.metric_type !== 'lagging'
  );

  if (!missingMetricType.length) {
    return buildLocalCheck('leading_lagging_metric_check', true);
  }

  return buildLocalCheck('leading_lagging_metric_check', false);
};

const getLocalMetricValueCheck = (
  workstream: Workstream
): WorkstreamCheckResponseWithFailureCopy | null => {
  const metrics = getVisibleHealthMetrics(workstream);
  if (!metrics.length) {
    return buildLocalCheck('metric_value_check', false);
  }

  const hasInvalidValues = getOpenHealthMetrics(metrics).some(
    (metric) =>
      hasInvalidMetricBaselineValue(metric.baseline) ||
      hasInvalidMetricCurrentValue(metric.current) ||
      (metric.metric_forecasts ?? [])
        .flatMap((forecast) => [forecast.actual, forecast.target, forecast.budget])
        .some((value) => hasInvalidMetricForecastValue(value))
  );

  if (!hasInvalidValues) {
    return buildLocalCheck('metric_value_check', true);
  }

  return buildLocalCheck('metric_value_check', false);
};

const getLocalMetricBaselineBudgetValueCheck = (
  workstream: Workstream
): WorkstreamCheckResponseWithFailureCopy | null => {
  const metrics = getVisibleHealthMetrics(workstream);
  if (!metrics.length) {
    return buildLocalCheck('metric_baseline_budget_value_check', false);
  }

  const requiredBudgetHalf = getRequiredBudgetHalf();
  const failingMetrics = getOpenHealthMetrics(metrics).filter(
    (metric) =>
      !hasNonEmptyText(metric.baseline) || !hasBudgetForRequiredHalf(metric, requiredBudgetHalf)
  );

  if (!failingMetrics.length) {
    return buildLocalCheck('metric_baseline_budget_value_check', true);
  }

  return buildLocalCheck('metric_baseline_budget_value_check', false);
};

const getLocalMetricMilestoneCheck = (workstream: Workstream) =>
  buildLocalCheck('metric_milestone_check', getVisibleMetricsOrMilestones(workstream).length > 0);

const getLocalHealthScoreChecks = (
  workstream: Workstream
): WorkstreamCheckResponseWithFailureCopy[] =>
  [
    getLocalObjectivePrecheck(workstream),
    getLocalExecSponsorCheck(workstream),
    getLocalNorthStarMetricCheck(workstream),
    getLocalLeadingLaggingMetricCheck(workstream),
    getLocalMetricValueCheck(workstream),
    getLocalMetricBaselineBudgetValueCheck(workstream),
    getLocalMetricMilestoneCheck(workstream),
  ].filter((check): check is WorkstreamCheckResponseWithFailureCopy => Boolean(check));

const getMnpiServerLocalHealthScoreChecks = (
  workstream: WorkstreamWithMnpiServerLocalHealthScoreChecks
): WorkstreamCheckResponseWithFailureCopy[] =>
  (workstream.mnpi_server_local_health_score_checks ?? []).map((check) => ({
    ...check,
    failureCopy:
      check.passed === false
        ? MNPI_SERVER_LOCAL_HEALTH_SCORE_FAILURE_COPY
        : getFailureCopy(check.id),
  }));

export const getLocalHealthScoreChecksWithMnpiServerOverrides = (
  workstream: WorkstreamWithMnpiServerLocalHealthScoreChecks
): WorkstreamCheckResponseWithFailureCopy[] => {
  const checksById = new Map(
    getLocalHealthScoreChecks(workstream).map((check) => [check.id, check])
  );

  getMnpiServerLocalHealthScoreChecks(workstream).forEach((check) => {
    checksById.set(check.id, check);
  });

  return [...checksById.values()];
};

const normalizeCheckResultSource = (
  check: WorkstreamCheckResponse
): WorkstreamCheckResponseWithSource => ({
  ...check,
  // The UI only needs to know when a result came from a local check. Older
  // responses omit this field, and any non-local value should render like a
  // normal server/AI result.
  resultSource: check.resultSource === 'local' ? 'local' : undefined,
});

export const getLocalHealthScoreChecksToDisplay = (
  workstream: WorkstreamWithMnpiServerLocalHealthScoreChecks,
  enabledGrowthbookFeatureIds: ReadonlySet<string> = new Set()
) => {
  const localChecksById = new Map(
    getLocalHealthScoreChecksWithMnpiServerOverrides(workstream).map((check) => [check.id, check])
  );
  const localChecksForDisplay = getWorkstreamCheckIdsForType(
    'health_score',
    enabledGrowthbookFeatureIds
  ).map((checkId) => {
    const checkSource = getWorkstreamCheckSource(checkId, 'health_score');
    const localCheck = localChecksById.get(checkId);

    if (checkSource === 'local_only') {
      return markLocalCheckResult(localCheck ?? buildLocalCheck(checkId, false));
    }

    if (checkSource === 'local_precheck_and_ai' && localCheck?.passed === false) {
      return markLocalCheckResult(localCheck);
    }

    return buildPendingCheck(checkId);
  });
  return getChecksForDisplay(localChecksForDisplay, 'health_score', enabledGrowthbookFeatureIds);
};

export const getServerHealthScoreChecksToDisplay = (
  workstream: WorkstreamWithMnpiServerLocalHealthScoreChecks,
  checks: WorkstreamCheckResponse[],
  enabledGrowthbookFeatureIds: ReadonlySet<string> = new Set()
) => {
  const normalizedChecks = checks.map(normalizeCheckResultSource);

  return getChecksForDisplay(
    mergeChecksWithLocalData({
      checks: normalizedChecks,
      localChecks: getLocalHealthScoreChecksWithMnpiServerOverrides(workstream),
      checksType: 'health_score',
    }),
    'health_score',
    enabledGrowthbookFeatureIds
  );
};

export type WorkstreamOutcomesValidationMetric = {
  id?: number | string | null;
  draftId?: string;
  metricId?: number | null;
  type: string;
  status?: string | null;
  complete?: boolean | null;
  titleText?: string | null;
  title?: string | null;
  baseline?: string | null;
  current?: string | null;
  metricForecasts?: Record<string, MetricForecastValueFields>;
  metric_forecasts?: Array<MetricForecastValueFields & {period?: string | null}> | null;
  metricType?: 'leading' | 'lagging' | 'other';
  isNew?: boolean;
};

export const getOutcomeId = (metric: WorkstreamOutcomesValidationMetric): string =>
  metric.metricId ? metric.metricId.toString() : (metric.draftId ?? '');

export const getOutcomeTitleText = (metric: {titleText?: string | null; title?: string | null}) => {
  const title = metric.titleText ?? metric.title ?? '';
  return (parseAndExtractTextFromLexicalJson(title) || title).trim();
};

const getLocalMetricBaselineValueCheck = (
  metrics: WorkstreamOutcomesValidationMetric[]
): WorkstreamCheckResponseWithFailureCopy | null => {
  const metricRows = metrics.filter((metric) => metric.type === 'metric');
  if (!metricRows.length) {
    return buildLocalCheck('metric_baseline_value_check', true);
  }

  const invalidBaselineOrCurrentMetrics = metricRows.filter(
    (metric) =>
      hasInvalidMetricBaselineValue(metric.baseline, {requireExists: true}) ||
      hasInvalidMetricCurrentValue(metric.current)
  );
  if (!invalidBaselineOrCurrentMetrics.length) {
    return buildLocalCheck('metric_baseline_value_check', true);
  }

  return buildLocalCheck('metric_baseline_value_check', false);
};

const getOutcomesValidationMetricForecastValues = (
  metric: WorkstreamOutcomesValidationMetric
): Array<string | null | undefined> => {
  const forecasts = metric.metricForecasts
    ? Object.values(metric.metricForecasts)
    : (metric.metric_forecasts ?? []).filter((forecast) => !forecast.archived_at);

  return forecasts.flatMap((forecast) => [forecast.actual, forecast.target, forecast.budget]);
};

const getLocalMetricActualForecastValueCheck = (
  metrics: WorkstreamOutcomesValidationMetric[]
): WorkstreamCheckResponseWithFailureCopy | null => {
  const metricRows = metrics.filter((metric) => metric.type === 'metric');
  if (!metricRows.length) {
    return buildLocalCheck('metric_actual_forecast_value_check', true);
  }

  const invalidActualOrForecastMetrics = metricRows.filter((metric) =>
    getOutcomesValidationMetricForecastValues(metric).some((value) =>
      hasInvalidMetricForecastValue(value)
    )
  );
  if (!invalidActualOrForecastMetrics.length) {
    return buildLocalCheck('metric_actual_forecast_value_check', true);
  }

  return buildLocalCheck('metric_actual_forecast_value_check', false);
};

const getLocalOutcomesMilestoneReasonableBinaryCheck = (
  metrics: WorkstreamOutcomesValidationMetric[]
): WorkstreamCheckResponseWithFailureCopy | null => {
  const milestones = metrics.filter((metric) => metric.type === 'milestone');
  if (!milestones.length) {
    return buildLocalCheck('outcomes_milestone_reasonable_binary_check', false);
  }

  return null;
};

const getLocalOutcomesMilestoneConciseCheck = (
  metrics: WorkstreamOutcomesValidationMetric[]
): WorkstreamCheckResponseWithFailureCopy | null => {
  const milestones = metrics.filter((metric) => metric.type === 'milestone');
  if (!milestones.length) {
    return buildLocalCheck('outcomes_milestone_concise_check', false);
  }

  if (milestones.some((metric) => getOutcomeTitleText(metric).split(/\s+/).length > 30)) {
    return buildLocalCheck('outcomes_milestone_concise_check', false);
  }

  return null;
};

export const getLocalOutcomesValidationChecks = (
  metrics: WorkstreamOutcomesValidationMetric[],
  checksType: 'outcomes_validation_metrics' | 'outcomes_validation_outcomes'
): WorkstreamCheckResponseWithFailureCopy[] => {
  if (checksType === 'outcomes_validation_metrics') {
    return [
      getLocalMetricBaselineValueCheck(metrics),
      getLocalMetricActualForecastValueCheck(metrics),
    ].filter((check): check is WorkstreamCheckResponseWithFailureCopy => Boolean(check));
  }
  if (checksType === 'outcomes_validation_outcomes') {
    return [
      getLocalOutcomesMilestoneReasonableBinaryCheck(metrics),
      getLocalOutcomesMilestoneConciseCheck(metrics),
    ].filter((check): check is WorkstreamCheckResponseWithFailureCopy => Boolean(check));
  }
  return [];
};

export const getOutcomesValidationChecksToDisplay = (
  metrics: WorkstreamOutcomesValidationMetric[],
  checks: WorkstreamOutcomesValidationCheckResponse[] = [],
  checksType: 'outcomes_validation_metrics' | 'outcomes_validation_outcomes'
) => {
  return getChecksForDisplay(
    mergeChecksWithLocalData({
      checks,
      localChecks: getLocalOutcomesValidationChecks(metrics, checksType),
      checksType,
    }),
    checksType
  );
};
